import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, projectStatusConfig } from 'entities';

import { Alert, Button } from 'components/Blocks';

import { StartWaveModal } from './StartModal';

import { useProjectProfile } from '../../context';
import { useProject } from 'providers/Project';
import { useToggle } from 'hooks';
import { useRoutePush } from 'routes';

const relevantTypes = ['TRACKER_ORIGIN', 'TRACKER_SEQUENCE'];

function isProjectActive(status: string): boolean {
  return ![projectStatusConfig.done.code, projectStatusConfig.canceled.code].some(
    (code) => code === status,
  );
}

interface SequenceWaveCheck {
  wavesLeft: number;
}

export function TrackerInfo() {
  const { loadingProjects, projectList } = useProject();
  const { project, isOwner } = useProjectProfile();

  const goToProject = useRoutePush('projectProfile');

  const { t } = useLang();

  const isOrigin = project?.type === 'TRACKER_ORIGIN';
  const projectIsActive = isProjectActive(project?.status);

  const shouldNotRender = [
    loadingProjects,
    !isOwner,
    !relevantTypes.includes(project?.type),
    isOrigin && projectIsActive,
  ].some(Boolean);

  const {
    data: { wavesLeft },
    loading: loadingSequenceWaves,
  } = loaders.projects.projectOriginWavesLeft.use({
    blockLoad: shouldNotRender || isOrigin,

    defaultValue: {} as SequenceWaveCheck,

    params: {
      origin: project?.originProject as string,
    },
  });

  const { isOn, toggle } = useToggle();

  const renderConfig = useMemo(() => {
    if (shouldNotRender) return;

    const match = [
      {
        text: t('this-is-a-tracker-origin-there-is-a-currently-sequence-active'),
        actionText: t('view'),
        resolver: () => {
          if (!isOrigin) return;

          const activeSequence = projectList.find(
            (proj) => proj.originProject === project?.id && isProjectActive(proj.status),
          );

          if (activeSequence) return () => goToProject({ projectId: activeSequence.id });
        },
      },
      {
        text: t('this-is-an-active-tracker-sequence'),
        actionText: t('view-origin'),
        resolver: () => {
          if (isOrigin || !projectIsActive) return;

          return () => goToProject({ projectId: project.originProject! });
        },
      },
      {
        text: t(
          'this-tracker-has-achieved-the-total-number-of-waves-required-client-must-bid-again-to-renew-our-services',
        ),
        resolver: () => {
          const originEnd = isOrigin && !project.wavesLeft;
          const sequenceEnd = typeof wavesLeft === 'number' && !wavesLeft;

          return originEnd || sequenceEnd;
        },
      },
      {
        text: t('this-is-a-tracker-origin-there-are-waves-left-and-no-active-wave-start-one-now'),
        actionText: t('start-new-wave'),
        resolver: () => {
          const activeSequence = projectList.find(
            ({ originProject }) => originProject === project?.id,
          );

          const canStart = [
            isOrigin,
            project.wavesLeft,
            !activeSequence,
            !projectIsActive,
            project.status !== projectStatusConfig.canceled.code,
          ].every(Boolean);

          if (!canStart) return;

          return toggle;
        },
      },
      {
        text: t(
          'this-is-a-tracker-sequence-there-are-waves-left-on-the-tracker-and-no-active-wave-start-one-now',
        ),
        actionText: t('start-new-wave'),
        resolver: () => {
          if (isOrigin || projectIsActive || !wavesLeft) return;

          const activeSequence = projectList.find(
            (proj) =>
              proj.originProject === project?.originProject &&
              proj.id !== project?.id &&
              isProjectActive(proj.status),
          );

          if (activeSequence) return;

          return toggle;
        },
      },
    ].find(({ resolver }) => resolver());

    return match;
  }, [
    goToProject,
    shouldNotRender,
    project,
    projectList,
    isOrigin,
    projectIsActive,
    wavesLeft,
    toggle,
    t,
  ]);

  if (shouldNotRender || !renderConfig || loadingSequenceWaves) return null;

  const { resolver, text, actionText } = renderConfig;

  return (
    <>
      <StartWaveModal
        close={toggle}
        open={isOn}
        originProject={isOrigin ? project.id : project.originProject!}
      />

      <Alert
        level="info"
        className="animate-in slide-in-from-bottom-12"
        bodyClassName="flex gap-2 items-center"
      >
        {text}

        {actionText && (
          <Button className="ml-auto" variant="ghost" size="small" onClick={resolver()}>
            {actionText}
          </Button>
        )}
      </Alert>
    </>
  );
}
